import React from 'react';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

interface TeleprompterHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TeleprompterHelpModal: React.FC<TeleprompterHelpModalProps> = ({ isOpen, onClose }) => {
  const deviceDetection = useDeviceDetection();

  if (!isOpen) return null;

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black" onClick={onClose}>
      <div
        className="mx-4 max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-2xl font-bold">Teleprompter Modes</h2>

        <div className="space-y-4 text-sm">
          {/* Auto-scroll mode */}
          <div>
            <h3 className="mb-2 text-lg font-semibold">Auto-scroll Mode</h3>
            <p className="mb-2 text-gray-700">
              The text automatically scrolls from bottom to top at your configured speed.
            </p>
            <ul className="list-inside list-disc space-y-1 text-gray-600">
              <li>Adjust scroll speed with the speed slider</li>
              <li>Preview the scroll before recording</li>
              <li>Best for reading through the entire script smoothly</li>
            </ul>
          </div>

          {/* Manual-scroll mode */}
          <div>
            <h3 className="mb-2 text-lg font-semibold">Manual-scroll Mode</h3>
            <p className="mb-2 text-gray-700">You control when and how fast the text scrolls.</p>
            <ul className="list-inside list-disc space-y-1 text-gray-600">
              <li>Click and drag (or touch and drag) inside the video window to scroll the text</li>
              {deviceDetection.isDesktop && (
                <>
                  <li>Press Up/Down arrow to scroll backward/forward one line at a time</li>
                  <p>OR</p>
                  <li>Hold arrow keys to scroll continuously at your configured speed</li>
                </>
              )}
              <li>Speed slider controls keyboard scroll rate</li>
              <li>Best for controlling your own pace while recording</li>
            </ul>
          </div>

          {/* Slide-advance mode */}
          <div>
            <h3 className="mb-2 text-lg font-semibold">Slide-advance Mode</h3>
            <p className="mb-2 text-gray-700">The script is broken into sections.</p>
            <ul className="list-inside list-disc space-y-1 text-gray-600">
              <li>Click left/right arrow buttons to navigate between lines</li>
              {deviceDetection.isDesktop && (
                <>
                  <li>Press Left arrow to go to previous line</li>
                  <li>Press Right arrow to go to next line</li>
                </>
              )}
              <li>See your progress with the slide counter</li>
              <li>Best for patients who want to avoid text motion.</li>
            </ul>
          </div>

          {/* General controls */}
          <div>
            <h3 className="mb-2 text-lg font-semibold">General Controls</h3>
            <ul className="list-inside list-disc space-y-1 text-gray-600">
              <li>Text size slider: Adjust font size (all modes)</li>
              <li>Opacity slider: Balance between video visibility and text readability</li>
              <li>Preview button: Test your settings before recording</li>
              <li>Change mode anytime using the gear icon</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="rounded bg-[#730D0E] px-4 py-2 text-white transition-colors hover:bg-[#730D0E]/90"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};
